import { useState, useEffect, useCallback } from 'react'
import { get, put } from '../lib/api'

export interface Goal {
  talentId: string
  targetRoles: string[]
  companyTierPref: string[]
  styleMemo: string | null
  updatedAt: string
}

export function useGoal(talentId: string | null) {
  const [goal, setGoal] = useState<Goal | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const basePath = talentId ? `/api/v1/talents/${talentId}/goal` : null

  const load = useCallback(async () => {
    if (!basePath) return
    setLoading(true)
    setError(null)
    try {
      const data = await get<Goal>(basePath)
      setGoal(data)
    } catch (e) {
      if (e instanceof Error && e.message.includes('GOAL_NOT_FOUND')) {
        setGoal(null)
      } else {
        setError(e instanceof Error ? e.message : '알 수 없는 오류')
      }
    } finally {
      setLoading(false)
    }
  }, [basePath])

  useEffect(() => {
    if (!basePath) {
      setGoal(null)
      return
    }
    load()
  }, [basePath, load])

  const save = useCallback(
    async (patch: {
      target_roles?: string[]
      company_tier_pref?: string[]
      style_memo?: string
    }) => {
      if (!basePath) return
      setSaving(true)
      setError(null)
      setSaved(false)
      try {
        const data = await put<Goal>(basePath, patch)
        setGoal(data)
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (e) {
        setError(e instanceof Error ? e.message : '저장 실패')
      } finally {
        setSaving(false)
      }
    },
    [basePath],
  )

  return { goal, loading, error, saving, saved, reload: load, save }
}
